import {
  CustomerSubmissionResponse,
  NextStepHint,
  ReviewTrack,
  SubmissionOutcome,
} from "@/core/customers/entities/CustomerSubmission";

// ---------------------------------------------------------------------------
// DTO shape (private to the details/ layer)
// ---------------------------------------------------------------------------

export interface CustomerSubmissionResponseDto {
  businessKey: string;
  submissionOutcome: string;
  customerStatus: string;
  accountCreated: boolean;
  accountNumber: string | null;
  customerCode: string | null;
  blockingReason: string | null;
  reviewTrack: string | null;
  title: string;
  message: string;
  instructions: string[] | null;
  nextStepHint: string;
  reopenable: boolean;
}

// ---------------------------------------------------------------------------
// Mapping functions (explicit switch — no bracket notation)
// ---------------------------------------------------------------------------

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapSubmissionOutcome(raw: string): SubmissionOutcome {
  switch (raw) {
    case "ACCOUNT_CREATED":
      return SubmissionOutcome.ACCOUNT_CREATED;
    case "REGISTERED_PENDING_REVIEW":
      return SubmissionOutcome.REGISTERED_PENDING_REVIEW;
    case "REFUSED_SANCTIONS":
      return SubmissionOutcome.REFUSED_SANCTIONS;
    case "SCREENING_IN_PROGRESS":
      return SubmissionOutcome.SCREENING_IN_PROGRESS;
    default:
      // Unknown outcome → treat as not-yet-decided so the agent can retry.
      return SubmissionOutcome.SCREENING_IN_PROGRESS;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapReviewTrack(raw: string | null): ReviewTrack | null {
  if (raw === null) return null;
  switch (raw) {
    case "EDD_4EYES":
      return ReviewTrack.EDD_4EYES;
    case "L1_REVIEW":
      return ReviewTrack.L1_REVIEW;
    case "P4":
      return ReviewTrack.P4;
    default:
      return null;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapNextStepHint(raw: string): NextStepHint {
  switch (raw) {
    case "EDD_APPROVAL_REQUIRED":
      return NextStepHint.EDD_APPROVAL_REQUIRED;
    case "L1_AUTHORIZATION_REQUIRED":
      return NextStepHint.L1_AUTHORIZATION_REQUIRED;
    case "WAIT_WEBHOOK":
      return NextStepHint.WAIT_WEBHOOK;
    case "WAIT_SCREENING":
      return NextStepHint.WAIT_SCREENING;
    case "CONTACT_COMPLIANCE":
      return NextStepHint.CONTACT_COMPLIANCE;
    case "NONE":
      return NextStepHint.NONE;
    default:
      return NextStepHint.NONE;
  }
}

export function mapSubmissionDtoToEntity(
  dto: CustomerSubmissionResponseDto,
): CustomerSubmissionResponse {
  return {
    businessKey: dto.businessKey,
    submissionOutcome: mapSubmissionOutcome(dto.submissionOutcome),
    customerStatus: dto.customerStatus,
    accountCreated: !!dto.accountCreated,
    accountNumber: dto.accountNumber ?? null,
    customerCode: dto.customerCode ?? null,
    blockingReason: dto.blockingReason ?? null,
    reviewTrack: mapReviewTrack(dto.reviewTrack ?? null),
    title: dto.title ?? "",
    message: dto.message ?? "",
    instructions: Array.isArray(dto.instructions) ? dto.instructions : [],
    nextStepHint: mapNextStepHint(dto.nextStepHint),
    reopenable: !!dto.reopenable,
  };
}
